<template>
  <section>
    <PageTitle>
      <span v-html="$store.getters['app/getMenuName']('step')" />
    </PageTitle>

    <SwipeTab
      v-model="activeStepTabId"
      @change="onCategoryTabChange"
      :items="stepTabItems"
      :background-color="true"
      :loading="loading"
      :sticky="false"
      :bulkFlag="bulkFlag"
      ref="swipeTab"
    />

    <PageContent :loading="loading" :bottom-sticked="false">
      <Container>
        <section class="page-content-section">
          <Headline :level="2" v-if="currentStepName">
            {{ $domUtils.decode(currentStepName) }}
          </Headline>

          <div class="step-action">
            <div class="step-action-item">
              <WaveButton
                type="button"
                size="xsmall"
                :gray="true"
                :outline="true"
                @click="toCreate"
              >
                <i class="fas fa-plus text-primary"></i>
                新規作成
              </WaveButton>
            </div>
            <div class="step-action-item" v-if="isPlanner">
              <WaveButton
                type="button"
                size="xsmall"
                :gray="true"
                :outline="true"
                @click="$modal.show('download-task-csv')"
              >
                <i class="fas fa-download text-primary"></i>
                CSVダウンロード
              </WaveButton>
            </div>
            <div class="step-action-item" v-if="isPlanner && !bulkFlag">
              <WaveButton
                type="button"
                size="xsmall"
                :gray="true"
                :outline="true"
                @click="startControllerTaskBulk"
              >
                <i class="fas fa-exclamation-circle text-primary"></i>
                一括操作
              </WaveButton>
            </div>
            <div class="step-action-item" v-if="isPlanner && bulkFlag">
              <WaveButton
                type="button"
                size="xsmall"
                :gray="true"
                :outline="true"
                @click="endControllerTaskBulk"
              >
                <i class="fas fa-exclamation-circle text-primary"></i>
                一括操作をやめる
              </WaveButton>
            </div>
          </div>
        </section>
      </Container>

      <div class="tab" v-if="hasTasks">
        <div class="tab-list">
          <div
            class="tab-list-item"
            :class="{'is-active': currentItemTab === index, 'disabled': bulkFlag}"
            @click="[!bulkFlag ? onChangeItemTab(index) : '']"
            v-for="(task, index) in tasks"
            :key="task.task_type_id"
          >
            {{ $domUtils.decode(task.task_type_name) }}
            <p class="tab-list-badge" v-if="task.task_list">
              <span  
                class="text-danger"
                v-if="unDoneTaskLength(task.task_list) > 0"
              >
                {{ unDoneTaskLength(task.task_list) | addBrackets }}
              </span>
              <span v-else-if="isAllCompleted(task.task_list)">
                <i class="tab-list-icon text-success fas fa-check-circle"></i>
              </span>
            </p>
          </div>
        </div>

        <div class="tab-item-wrapper">
          <div
            class="tab-item"
            :class="[currentItemTab === index ? 'is-active' : '']"
            v-for="(taskList, index) in tasks"
            :key="`tasklist${index}`"
          >
            <div class="tab-item-body">
              <div class="task-list">
                <TaskList
                  :step-id="activeStepTabId"
                  :items="taskList.task_list"
                  :bulkFlag="bulkFlag"
                  @complete="onComplete"
                  @delete="onDeleteClick"
                  @revert="onRevert"
                  @checks="onChecks"
                />
              </div>

              <div
                v-if="isPlanner"
              >
                <div class="space-t-6 space-b-3 text-center">
                  <div class="step-button">
                  <WaveButton @click="openControllerTaskBulk" v-if="bulkFlag" :disabled="mergeCheckedStepTasks.length === 0">
                    チェックしたタスクを一括操作する
                  </WaveButton>
                  <WaveButton @click="showUnavailable" v-if="hasDeleteTask(taskList.task_list)" :disabled="bulkFlag">
                    削除したタスクを見る
                  </WaveButton>
                  </div>
                </div>

                <transition name="fade">
                  <div class="task-list" v-if="isShowUnavailable">
                    <TaskList
                      :step-id="activeStepTabId"
                      :items="taskList.task_list"
                      :deleted-task="true"
                      :has-top-border="true"
                      :bulkFlag="bulkFlag"
                      @complete="onComplete"
                      @revert="onRevert"
                      @checks="onDelChecks"
                    />
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageContent>

    <Modal
      id="download-task-csv"
      class="is-scrollable"
    >
      <template #default>
        <h2 class="headline space-b-1">CSVダウンロード</h2>
        <ul class="step-check-list">
          <li class="step-check-item">
            <label for="step-lock-all" class="checkbox">
              <input
                id="step-lock-all"
                type="checkbox"
                v-model="all"
                @change="onAllCheckToggle"
              />
              <span class="checkbox-text">全て選択</span>
            </label>
          </li>
        </ul>
        <ul class="step-check-list">
          <li
            v-for="step in steps"
            :key="step.step_id"
            class="step-check-item"
          >
            <label :for="`step-${step.step_id}`" class="checkbox">
              <input
                :id="`step-${step.step_id}`"
                type="checkbox"
                :value="step.step_id"
                v-model="checkedStep"
                @change="onCheckToggle"
              />
              <span class="checkbox-text"
                v-html="step.step_abbreviation"></span>
            </label>
          </li>
        </ul>
      </template>
      <template #footer>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          @click="$modal.close('download-task-csv')"
        >キャンセル</WaveButton>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          :disabled="checkedStep.length === 0"
          @click="onDownloadCSV"
        >ダウンロード</WaveButton>
      </template>

    </Modal>

    <Modal
      id="controller-task-bulk"
    >
      <template #default>
        <div class="form-group">
          <h2 class="headline space-b-1">一括操作</h2>
        </div>
        <div class="form-group">
          <label>処理</label>
          <div class="select is-fullwidth">
          <select
            class="form-control"
            v-model="processDivision"
            @change="onProcessDivision"
          >
            <option
              v-for="item in selectUpOrDel"
              :value="item.value"
              :key="item.name"
              v-html="item.name"
            />
          </select>
          </div>
        </div>
        <div class="form-group">
          <label>STEP</label>
          <div class="select is-fullwidth">
          <select
            id="step"
            class="form-control"
            v-model="selectStep"
            :disabled="processDivision === BULK_DELETE"
          >
            <option :value="0">更新しない</option>
            <option
              v-for="item in allSteps"
              :value="item.step_id"
              :key="item.step_abbreviation"
              v-html="item.step_abbreviation"
            />
          </select>
          </div>
        </div>
        <div class="form-group">
          <label>タスク種別</label>
          <div class="select is-fullwidth">
          <select
            class="form-control"
            v-model="selectTaskType"
            :disabled="processDivision === BULK_DELETE"
          >
            <option :value="0">更新しない</option>
            <option
              v-for="item in allTaskType"
              :value="item.task_type_id"
              :key="item.task_type_name"
              v-html="item.task_type_name"
            />
          </select>
          </div>
        </div>
      </template>
      <template #footer>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          @click="$modal.close('controller-task-bulk')"
        >キャンセル</WaveButton>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          :disabled="processDivision === BULK_UPDATE && selectStep === 0 && selectTaskType === 0"
          @click="bulkUpdate"
        >実行</WaveButton>
      </template>

    </Modal>

    <Modal
      id="modal-complete"
      title="一括操作が完了しました"
      icon="check"
      @ok="modalOk"
    />

    <Modal
      id="confirm-delete-task"
      title="削除してよろしいですか？"
      icon="exclamation"
      type="danger"
    >
      <template #footer>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          :gray="true"
          @click="$modal.close('confirm-delete-task')"
        >キャンセル</WaveButton>
        <WaveButton
          type="button"
          size="small"
          :text="true"
          :danger="true"
          @click="onDelete"
        >OK</WaveButton>
      </template>
    </Modal>
    
  </section>
</template>

<script>
import moment from 'moment'
import Modal from '@/components/Modal'
import SwipeTab from '@/components/SwipeTab.vue'
import TaskList from '@/components/TaskList.vue'
import stepApi from '@/api/step'
import { renderError } from '@/utils/error'

const BULK_UPDATE = 1
const BULK_DELETE = 2
const selectUpOrDel = [
  { value: 1, name: '一括更新' },
  { value: 2, name: '一括削除' },
]

export default {
  components: {
    SwipeTab,
    TaskList,
    Modal
  },

  metaInfo() {
    return {
      title: this.$store.state.app.pageName,
      bodyAttrs: { class: 'step' }
    }
  },

  data() {
    return {
      activeStepTabId: 0,
      steps: [],
      allSteps: [],
      checkedStep: [],
      tasks: [],
      allTaskType: [],
      tabOffset: { 1: 0, 2: 0 },
      currentItemTab: 0,
      isShowUnavailable: false,
      loading: false,
      deleteTarget: null,
      all: false,
      selectUpOrDel,
      processDivision: [],
      BULK_UPDATE,
      BULK_DELETE,
      selectStep: [],
      selectTaskType: [],
      checkedStepTasks: [],
      delCheckedStepTasks: [],
      mergeCheckedStepTasks: [],
      bulkFlag: false,
      stepTaskStatusBulk: {
        process_division: [],
        step_id: [],
        type_id: [],
        task_status_list: [],
      },
    }
  },

  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    stepTabItems() {
      return this.steps.map(step => ({
        key: step.step_id,
        label: this.$domUtils.decode(step.step_abbreviation)
      }))
    },
    currentStepName() {
      const step = this.steps.find(
        ({ step_id }) => step_id === this.activeStepTabId
      )
      if (!step) {
        return false
      }
      return step.step_name
    },
    isPlanner() {
      return this.$store.getters['auth/isPlanner']
    },
    isCustomer() {
      return this.$store.getters['auth/isCustomer']
    },
    idParam() {
      return this.$route.params.id
    },
    hasTasks() {
      return this.tasks.length > 0
    },
    csvFileName() {
      const current = moment().format('YYYYMMDDHHmmss')
      return `タスク一覧_${current}.csv`
    }
  },

  filters: {
    addBrackets(val) {
      return `(${val})`
    }
  },

  methods: {
    // 画面初期化
    async fetchStepPage() {
      await this.fetchStep()
      await this.fetchAllStep()
      await this.fetchTaskList()

      // 保持していたタブに切り替え
      this.currentItemTab = this.$store.getters['step/stepTabIndex']
    },
    async fetchStep() {
      try {
        this.steps = await stepApi.getStep(this.businessId, { task: `true` })
      } catch(err) {
        renderError(err)
      }
    },
    async fetchAllStep() {
      // タスクが存在しないSTEPも取得
      try {
        this.allSteps = await stepApi.getStep(this.businessId, { task: `false` })
      } catch (err) {
        renderError(err)
      }
    },
    async fetchTaskList() {
      const hasIdParam = typeof this.idParam !== 'undefined'

      if (!hasIdParam && this.steps.length === 0) {
        return false
      }

      try {
        var stepId = hasIdParam ? this.idParam : this.steps[0].step_id
        var isStepExist = false
        for (var i = 0; i < this.steps.length; i++) {
          // 更新前のSTEPが取得したSTEPリストに存在するとtrue
          if (this.steps[i].step_id == this.idParam) {
            isStepExist = true
            break
          } 
        }
        if (!isStepExist) {
          // 更新前のSTEPが取得したSTEPリストに存在しなかったら一番目のSTEPに遷移
          stepId = this.steps[0].step_id
        }
        this.tasks = await stepApi.getTaskList(this.businessId, stepId)
        this.activeStepTabId = stepId * 1
      } catch(err) {
        renderError(err)
      } finally {
        this.loading = false
      }
    },

    // 削除
    onDeleteClick(taskId, taskStatusId, taskTypeId, callback) {
      this.$modal.show('confirm-delete-task', {
        onClose: () => {
          this.deleteTarget = null
          callback()
        }
      })

      this.deleteTarget = this.selectedTask(taskId, taskStatusId, taskTypeId)
    },
    async onDelete() {
      if (this.deleteTarget === null) {
        return false
      }

      this.deleteTarget.unavailable = true

      try {
        const response = await stepApi.delete(
          this.businessId,
          this.deleteTarget.task_status_id,
          this.deleteTarget
        )

        if (!this.hasStatus(this.deleteTarget.task_status_id)) {
          this.deleteTarget.task_status_id = response.task_status_id
        }
        this.deleteTarget.revision = response.revision
      } catch(err) {
        renderError(err)
      } finally {
        this.$modal.close('confirm-delete-task')
      }
    },

    // 元に戻す
    async onRevert(taskId, taskStatusId, taskTypeId) {
      const task = this.selectedTask(taskId, taskStatusId, taskTypeId)
      task.unavailable = false

      try {
        const response = await stepApi.delete(
          this.businessId,
          task.task_status_id,
          task
        )

        if (!this.hasStatus(task.task_status_id)) {
          task.task_status_id = response.task_status_id
        }
        task.revision = response.revision
      } catch(err) {
        renderError(err)
      }
    },

    // 完了
    async onComplete(taskId, taskStatusId, taskTypeId, callback) {
      const task = this.selectedTask(taskId, taskStatusId, taskTypeId)
      task.complete = !task.complete

      try {
        const response = await stepApi.complete(
          this.businessId,
          task.task_status_id,
          task
        )

        if (!this.hasStatus(task.task_status_id)) {
          task.task_status_id = response.task_status_id
        }
        task.revision = response.revision

        callback()
      } catch(err) {
        renderError(err)
      }
    },

    async onDownloadCSV() {
      try {
        const response = await stepApi.download(this.businessId, this.checkedStep)
        this.$fileSaver(response, "text/csv", this.csvFileName)
        this.$modal.close('download-task-csv')
      } catch(err) {
        renderError(err)
      }
    },

    // クリックしたタスクの抽出
    selectedTask(taskId, taskStatusId, taskTypeId) {
      const currentTypeTask = this.tasks.find(
        ({ task_type_id }) => task_type_id === taskTypeId
      )

      let task = []

      if (taskStatusId !== 0) {
        task = currentTypeTask.task_list.find(
          ({ task_status_id }) => task_status_id === taskStatusId
        )
      } else {
        task = currentTypeTask.task_list.find(
          ({ task_id }) => task_id === taskId
        )
      }

      return task
    },
    showUnavailable() {
      this.isShowUnavailable = !this.isShowUnavailable
    },
    unDoneTaskLength(items) {
      return items.filter(item => !item.complete && !item.unavailable).length
    },
    deletedTaskLength(items) {
      return items.filter(item => item.unavailable).length
    },
    hasDeleteTask(items) {
      return items.filter(item => item.unavailable).length > 0
    },
    isAllCompleted(items) {
      const isComplete = t => t.complete && !t.unavailable
      const activeTasks = items.length - this.deletedTaskLength(items)
      const isCompleteAll = items.filter(isComplete).length === activeTasks

      return activeTasks !== 0 && isCompleteAll
    },

    // タブ切り替え
    onChangeItemTab(nextItemTab) {
      this.currentItemTab = nextItemTab

      // 切り替え後のタブインデックスを保持しておく
      this.$store.commit('step/SET_STEP_TAG', this.currentItemTab)
    },

    // スワイプタブ切り替え
    onCategoryTabChange(id) {
      if (this.loading) {
        return false
      }

      window.scrollTo(0, 0)
      this.loading = true
      this.$store.commit('step/SET_STEP_TAG', 0)
      this.$wcRouter.push({ name: 'step.index', params: { id } })
    },

    // ダウンロードモーダルのチェックボックス
    onAllCheckToggle() {
      this.checkedStep = this.all
        ? this.steps.map(step => step.step_id)
        : []
    },
    onCheckToggle() {
      this.all = this.checkedStep.length === this.steps.length
    },

    toCreate() {
      this.$wcRouter.push({
        name: 'step.task.create',
        params: { stepId: this.activeStepTabId }
      })
    },

    hasStatus(id) {
      return id !== 0
    },

    onChecks(check) {
      this.checkedStepTasks = check
      this.mergeCheckedStepTasks = this.delCheckedStepTasks.concat(this.checkedStepTasks)
    },

    onDelChecks(delCheck) {
      this.delCheckedStepTasks = delCheck
      this.mergeCheckedStepTasks = this.checkedStepTasks.concat(this.delCheckedStepTasks)
    },

    onClearChecks() {
      this.checkedStepTasks.splice(-this.checkedStepTasks.length)
      this.delCheckedStepTasks.splice(-this.delCheckedStepTasks.length)
      this.mergeCheckedStepTasks.splice(-this.mergeCheckedStepTasks.length)
    },

    onProcessDivision() {
      if (this.processDivision === BULK_DELETE) {
        this.selectStep = 0
        this.selectTaskType = 0
      }
    },

    startControllerTaskBulk() {
      this.bulkFlag = true
      this.onClearChecks()
      this.isShowUnavailable = true
    },

    endControllerTaskBulk() {
      this.bulkFlag = false
      this.onClearChecks()
    },

    async openControllerTaskBulk() {
      this.processDivision = BULK_UPDATE
      this.selectStep = 0
      this.selectTaskType = 0
      this.allTaskType = await stepApi.getTaskTypes(this.businessId)
      this.$modal.show('controller-task-bulk')
    },

    async bulkUpdate() {
      this.loading = true
      this.stepTaskStatusBulk.process_division = this.processDivision
      this.stepTaskStatusBulk.step_id = this.selectStep
      this.stepTaskStatusBulk.type_id = this.selectTaskType
      this.stepTaskStatusBulk.task_status_list = this.mergeCheckedStepTasks
      try {
        await stepApi.updateStatusBulk(
          this.businessId,
          this.stepTaskStatusBulk
        )
        this.$modal.close('controller-task-bulk')
        this.$modal.show('modal-complete')
      } catch(err) {
        renderError(err)
      } finally {
        this.loading = false
      }
    },
    modalOk() {
      this.$modal.close('modal-complete')
      this.fetchStepPage()
      this.bulkFlag = false
    },
  },

  created() {    
    this.$withLoading(this.fetchStepPage)
  },

  watch: {
    $route() {
      // タスク種別タブの選択状態を初期化
      this.currentItemTab = 0

      // ブラウザバックでのステップ切り替えかどうかを判定
      if (!this.loading) {
        this.loading = true
        this.fetchStepPage().then(() => this.$refs.swipeTab.initCarousel())
        return false
      }

      this.fetchStepPage()
    }
  }
}
</script>
